/**
 * HustleMeetPostEventPage.jsx — /meet/events/:eventId/wrap-up
 *
 * One page, two forms:
 *   - Private survey (Matchmaker learning data + mutual-no votes)
 *   - Public review (surfaces on the vendor page + Google/FB deep-link
 *     syndication offered when rating ≥ 4)
 *
 * Endpoints:
 *   POST /api/hustlemeet/surveys/{eventId}
 *   POST /api/hustlemeet/reviews/{eventId}   (returns syndication_urls)
 *   POST /api/hustlemeet/reviews/{id}/syndicated
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Star, Users, MessageSquare, Send, ExternalLink,
         CheckCircle2, ClipboardCopy } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function StarPicker({ value, onChange, testId, size = 26 }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1 rounded-full transition"
          data-testid={`${testId}-${n}`}
        >
          <Star
            size={size}
            className={n <= (value || 0) ? "fill-amber-500 text-amber-500" : "text-stone-300"}
          />
        </button>
      ))}
    </div>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 h-9 rounded-full text-xs font-bold border transition ${
        active
          ? "bg-amber-500 border-amber-500 text-slate-950"
          : "bg-white dark:bg-slate-950 border-stone-200 dark:border-stone-700 text-stone-500 hover:border-amber-400"
      }`}
    >{label}</button>
  );
}

export default function HustleMeetPostEventPage() {
  const { eventId } = useParams();
  const [attendees, setAttendees] = useState([]);       // peers we could vote on
  const [conv,    setConv]    = useState(0);
  const [venue,   setVenue]   = useState(0);
  const [match,   setMatch]   = useState(0);
  const [votes,   setVotes]   = useState({});            // {peer_id: yes|no|maybe}
  const [vibe,    setVibe]    = useState("");
  const [concerns,setConcerns]= useState("");
  const [noShow,  setNoShow]  = useState(false);
  const [surveyDone,setSurveyDone]= useState(false);

  // Review
  const [rating,     setRating]     = useState(0);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewTitle,setReviewTitle]= useState("");
  const [reviewResp, setReviewResp] = useState(null);
  const [busy,       setBusy]       = useState(false);

  useEffect(() => {
    document.title = "HustleMeet · Wrap up your dinner";
    // Attempt to fetch fellow attendees (best-effort; may be empty).
    // We reuse /api/hustle-events/{id}/attendees if it exists; fallback to []
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/hustle-events/${eventId}/attendees`,
          { headers: authHeader(), withCredentials: true });
        setAttendees(data?.attendees || []);
      } catch {
        setAttendees([]);
      }
    })();
  }, [eventId]);

  const setVote = (peerId, v) =>
    setVotes({ ...votes, [peerId]: votes[peerId] === v ? undefined : v });

  const submitSurvey = async () => {
    if (!conv && !venue && !match && !noShow && Object.keys(votes).length === 0) {
      toast.error("Please answer at least one rating or peer vote.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/hustlemeet/surveys/${eventId}`,
        {
          conversation_rating_1_to_5: conv || undefined,
          venue_rating_1_to_5:        venue || undefined,
          match_rating_1_to_5:        match || undefined,
          would_dine_again:           Object.fromEntries(
            Object.entries(votes).filter(([_, v]) => v)),
          vibe_words:                 vibe,
          concerns:                   concerns,
          no_show:                    noShow,
        },
        { headers: authHeader(), withCredentials: true });
      toast.success("Thanks — the Matchmaker just learned from your table.");
      setSurveyDone(true);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info("You've already submitted a survey for this dinner.");
        setSurveyDone(true);
      } else {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitReview = async () => {
    if (!rating) { toast.error("Give the dinner a star rating."); return; }
    if (reviewBody.trim().length < 20) {
      toast.error("Add a little more to your review (20+ characters).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hustlemeet/reviews/${eventId}`,
        { rating, title: reviewTitle, body: reviewBody },
        { headers: authHeader(), withCredentials: true });
      setReviewResp(data);
      toast.success("Review posted — thanks!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const postYourReview = async () => {
    // Copy the review text to clipboard first (best-effort).
    try {
      await navigator.clipboard.writeText(reviewBody);
      toast.info("Review copied to your clipboard. Paste on Google to submit.");
    } catch { /* clipboard optional */ }
    const url = reviewResp?.syndication_urls?.google;
    if (!url) {
      toast.error("This vendor hasn't linked their Google profile yet.");
      return;
    }
    // Mark syndicated (best-effort).
    try {
      await axios.post(
        `${API}/hustlemeet/reviews/${reviewResp.review.id}/syndicated`,
        { platform: "google" },
        { headers: authHeader(), withCredentials: true });
    } catch { /* silent */ }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="hustlemeet-postevent-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleMeet · Wrap up",
                     to: `/meet/events/${eventId}/wrap-up` }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24 space-y-8">
        <header>
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            After-dinner survey
          </p>
          <h1 className="mt-2 text-3xl font-black">How was your table?</h1>
          <p className="mt-2 text-sm text-stone-500">
            Two minutes now teaches the Matchmaker for next time — and lets us thank
            the venue publicly if you loved it.
          </p>
        </header>

        {/* Private survey */}
        <section
          data-testid="survey-section"
          className={`rounded-2xl border p-6 ${
            surveyDone
              ? "border-emerald-300 dark:border-emerald-800 bg-emerald-500/5"
              : "border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950"
          }`}
        >
          <div className="flex items-center gap-2">
            {surveyDone
              ? <CheckCircle2 size={20} className="text-emerald-500"/>
              : <MessageSquare size={20} className="text-amber-500"/>}
            <h2 className="font-black text-lg">Private survey (Matchmaker only sees this)</h2>
          </div>

          {surveyDone ? (
            <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">
              Thanks — recorded. The Matchmaker will use this for future tables.
            </p>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-1">Conversation</p>
                  <StarPicker value={conv} onChange={setConv} testId="stars-conv"/>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-1">Venue</p>
                  <StarPicker value={venue} onChange={setVenue} testId="stars-venue"/>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-1">Match quality</p>
                  <StarPicker value={match} onChange={setMatch} testId="stars-match"/>
                </div>
              </div>

              {attendees.length > 0 && (
                <div className="mt-5" data-testid="peer-votes">
                  <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-2">
                    <Users size={12} className="inline mr-1"/> Would you dine with them again?
                  </p>
                  <ul className="space-y-2">
                    {attendees.map((a) => (
                      <li
                        key={a.id || a.member_id}
                        data-testid={`peer-row-${a.id || a.member_id}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 dark:border-stone-800 p-3"
                      >
                        <span className="font-semibold">{a.display_name || a.first_name || "Fellow guest"}</span>
                        <div className="flex gap-1.5">
                          {["yes", "maybe", "no"].map((v) => (
                            <Chip
                              key={v}
                              label={v}
                              active={votes[a.id || a.member_id] === v}
                              onClick={() => setVote(a.id || a.member_id, v)}
                              testId={`vote-${a.id || a.member_id}-${v}`}
                            />
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">3 words for the vibe</label>
                  <input
                    data-testid="input-vibe"
                    className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                    value={vibe} onChange={(e) => setVibe(e.target.value)}
                    placeholder="warm, curious, funny"
                  />
                </div>
                <label className="flex items-center gap-2 self-end pb-2">
                  <input
                    type="checkbox"
                    checked={noShow}
                    onChange={(e) => setNoShow(e.target.checked)}
                    data-testid="chk-no-show"
                  />
                  <span className="text-sm">I couldn&apos;t make it (no-show)</span>
                </label>
              </div>
              <div className="mt-4">
                <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">
                  Concerns to raise privately (mods only)
                </label>
                <textarea
                  data-testid="input-concerns"
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                  value={concerns}
                  onChange={(e) => setConcerns(e.target.value)}
                  placeholder="Anything the moderation team should know."
                />
              </div>
              <div className="mt-5 flex justify-end">
                <Button
                  onClick={submitSurvey} disabled={busy}
                  data-testid="btn-submit-survey"
                  className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                >
                  <Send size={14} className="mr-1"/>
                  {busy ? "Saving…" : "Submit survey"}
                </Button>
              </div>
            </>
          )}
        </section>

        {/* Public review */}
        <section
          data-testid="review-section"
          className={`rounded-2xl border p-6 ${
            reviewResp
              ? "border-emerald-300 dark:border-emerald-800 bg-emerald-500/5"
              : "border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950"
          }`}
        >
          <div className="flex items-center gap-2">
            {reviewResp
              ? <CheckCircle2 size={20} className="text-emerald-500"/>
              : <Star size={20} className="text-amber-500"/>}
            <h2 className="font-black text-lg">Public review (helps other members choose)</h2>
          </div>

          {!reviewResp && (
            <>
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-1">Your rating</p>
                <StarPicker value={rating} onChange={setRating} testId="stars-review" size={30}/>
              </div>
              <div className="mt-4">
                <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Title (optional)</label>
                <input
                  data-testid="input-review-title"
                  className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  placeholder="A warm night, brilliant pasta"
                />
              </div>
              <div className="mt-4">
                <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">What made it special?</label>
                <textarea
                  data-testid="input-review-body"
                  rows={4}
                  className="mt-1 w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                  value={reviewBody}
                  onChange={(e) => setReviewBody(e.target.value)}
                  placeholder="The chef came out to explain each course… the couple next to us made me laugh so hard I cried…"
                />
                <p className="mt-1 text-[10px] text-stone-500">Minimum 20 characters.</p>
              </div>
              <div className="mt-5 flex justify-end">
                <Button
                  onClick={submitReview} disabled={busy || reviewBody.length < 20}
                  data-testid="btn-submit-review"
                  className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                >
                  <Send size={14} className="mr-1"/>
                  {busy ? "Posting…" : "Post review"}
                </Button>
              </div>
            </>
          )}

          {reviewResp && (
            <>
              <p className="mt-3 text-sm">Live on the vendor page — thank you!</p>
              {reviewResp.syndicate_prompt && (
                <div
                  data-testid="syndication-panel"
                  className="mt-5 rounded-2xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-5"
                >
                  <p className="font-black">
                    Your {reviewResp.review.rating}-star review would help the venue on Google too. Post it once more?
                  </p>
                  <p className="mt-2 text-xs text-stone-500">
                    We&apos;ll copy your text to your clipboard and open the venue&apos;s Google review page — one tap to paste and submit.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {reviewResp.syndication_urls?.google ? (
                      <Button
                        onClick={postYourReview}
                        data-testid="btn-post-your-review"
                        className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                      >
                        <ClipboardCopy size={13} className="mr-1"/> Post your review on Google
                        <ExternalLink size={12} className="ml-1"/>
                      </Button>
                    ) : (
                      <p className="text-xs text-stone-500">
                        Vendor hasn&apos;t linked their Google profile yet — thanks anyway!
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
